import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { EmptyState, PageHeader } from '@/components/page-header';
import { ProjectDialog } from '@/components/tracking/project-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useMe } from '@/lib/clients';
import { formatAmount, formatDate } from '@/lib/format';
import { sourceLabel } from '@/lib/journal';
import { useOrgId } from '@/lib/org';
import { PROJECT_STATUS_LABEL, useProject, useProjectTransactions } from '@/lib/tracking';

export const Route = createFileRoute('/projects/$projectId')({ component: ProjectPage });

/** Screen PR2 (document 06): one project's figures and transactions from the ledger. */
function ProjectPage() {
  const { projectId } = Route.useParams();
  const orgId = useOrgId();
  const me = useMe();
  const navigate = useNavigate();
  const project = useProject(projectId);
  const txns = useProjectTransactions(projectId);
  const [editing, setEditing] = useState(false);
  if (!orgId) return <EmptyState title="Select a client" />;
  if (project.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (project.isError)
    return <EmptyState title="Could not open the project" hint={project.error.message} />;
  const p = project.data;
  const income = Number(p.income);
  const margin = income !== 0 ? `${((Number(p.profit) / income) * 100).toFixed(1)}%` : '—';
  const year = new Date().getFullYear();
  return (
    <div>
      <PageHeader
        title={p.name}
        description={`${p.customerName}${p.startDate ? ` · from ${formatDate(p.startDate)}` : ''}${p.endDate ? ` to ${formatDate(p.endDate)}` : ''}`}
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() =>
                void navigate({
                  to: '/reports/$reportKey',
                  params: { reportKey: 'profit-and-loss' },
                  search: { projects: p.id, from: p.startDate ?? `${year}-01-01` },
                })
              }
            >
              Profit and loss
            </Button>
            {me.data?.isStaff ? <Button onClick={() => setEditing(true)}>Edit</Button> : null}
          </div>
        }
      />
      <div className="mb-4 flex items-center gap-2">
        <Badge variant="outline">{PROJECT_STATUS_LABEL[p.status]}</Badge>
        {p.description ? (
          <span className="text-sm text-muted-foreground">{p.description}</span>
        ) : null}
      </div>
      <dl className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-5" data-testid="project-figures">
        {[
          ['Income', formatAmount(p.income)],
          ['Costs', formatAmount(p.costs)],
          ['Profit', formatAmount(p.profit)],
          ['Margin', margin],
          ['Open balance', formatAmount(p.openBalance)],
        ].map(([label, value]) => (
          <div key={label} className="rounded-lg border bg-card p-3">
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
            <dd className="tnum text-xl font-semibold">
              {label === 'Margin' ? value : `${p.currency} ${value}`}
            </dd>
          </div>
        ))}
      </dl>
      <h2 className="mb-2 text-sm font-semibold">Transactions</h2>
      {txns.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !txns.data?.length ? (
        <EmptyState
          title="No transactions yet"
          hint="Choose this project on an invoice, bill, expense or journal entry."
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>No.</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Memo</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {txns.data.map((t) => (
                <TableRow key={t.entryId}>
                  <TableCell>{formatDate(t.date)}</TableCell>
                  <TableCell>
                    <Link
                      to="/accounting/journal-entries/$entryId"
                      params={{ entryId: t.entryId }}
                      className="hover:text-primary hover:underline"
                    >
                      {sourceLabel(t.sourceType)}
                    </Link>
                  </TableCell>
                  <TableCell>{t.number ?? ''}</TableCell>
                  <TableCell>{t.contactName ?? ''}</TableCell>
                  <TableCell className="max-w-[320px] truncate">{t.memo ?? ''}</TableCell>
                  <TableCell className="tnum text-right">{formatAmount(t.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <ProjectDialog project={editing ? p : null} onClose={() => setEditing(false)} />
    </div>
  );
}
